import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Card, Typography, message, Spin, Button, Select, Space, Progress, Tag, Modal } from 'antd';
import { FileTextOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Uploader } from "@fastpix/resumable-uploads";
import { Context } from "../../context";
import { convertIdToKey } from "../../utils";
import contentIds from "./contentIds.json";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;


const ManageContentIds = () => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploader, setCurrentUploader] = useState(null);
  const [contentExists, setContentExists] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const { token, apiUrl } = useContext(Context);

    useEffect(() => {
    const ids = contentIds.map(item => item.content_id);
    setData(ids);
  }, []);

  const fetchAllSongs = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/songs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const songsData = convertIdToKey(result.songs);

      // Filter songs that don't have playbackStatusFp key
      const filtered = songsData.filter(each => !each.playbackStatusFp);
      setFilteredSongs(filtered);

    } catch (error) {
      console.error('Error fetching songs:', error);

    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  // Fetch all songs on component mount
  useEffect(() => {
    fetchAllSongs();
  }, [fetchAllSongs]);

  // Get presigned URL for upload
  const getPresignedUrl = async (params) => {
    try {
      const response = await fetch(`${apiUrl}/fpvideo/getPresignedUrl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (data.success) {
        return data.data.url;
      } else {
        message.error(data.error || "Failed to get presigned URL");
      }
    } catch (error) {
      message.error("Failed to get upload URL");
    }
  };

  // Refresh selected song content
  const refreshContent = async () => {
    if (!selectedSong) return;

    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const response = await fetch(`${apiUrl}/songs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      const songsData = convertIdToKey(result.songs);
      const updatedSong = songsData.find(song => song.key === selectedSong.key);

      if (updatedSong) {
        setSelectedSong(updatedSong);
        // Check if content now exists
        const hasPlaybackStatus = updatedSong.playbackStatusFp;
        setContentExists(hasPlaybackStatus ? "ready" : null);
      }
    } catch (error) {
      console.error('Error refreshing content:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle video upload
  const handleVideoUpload = async (file) => {
    if (!selectedSong) {
      message.error("Please select a song first");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadParams = {
        content_id: selectedSong.key,
        content_type: "song",
        accessibility: "drm",
        series_id: null,
        season: null,
        episode: null,
      };

      const signedUrl = await getPresignedUrl(uploadParams);

      const fileUploader = Uploader.init({
        endpoint: signedUrl,
        file: file,
        chunkSize: 153600,
        retryAttempts: 5,
      });

      setCurrentUploader(fileUploader);

      fileUploader.on("progress", (event) => {
        const percentage = Math.round(event.detail.progress);
        setUploadProgress(percentage);
      });

      fileUploader.on("success", () => {
        setUploadProgress(100);
        setUploading(false);
        setCurrentUploader(null);
        message.success("Video uploaded successfully!");
        setRefreshing(true);
        refreshContent();
        setContentExists("processing");
      });

      fileUploader.on("error", (event) => {
        setUploading(false);
        setCurrentUploader(null);
        message.error(
          "Video upload failed: " +
          (event?.detail?.message || "Unknown error")
        );
      });
    } catch (error) {
      setUploading(false);
      setCurrentUploader(null);
      message.error("Failed to start video upload");
    }
  };

  // Delete content
  const handleDeleteContent = () => {
    if (!selectedSong) {
      message.error("Please select a song first");
      return;
    }

    confirm({
      title: "Are you sure you want to delete this content?",
      content: "This action will permanently delete the content and cannot be undone.",
      icon: <ExclamationCircleFilled />,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const params = new URLSearchParams({
            content_id: selectedSong.key,
            content_type: "song",
            media_id_fp: selectedSong.mediaIdFp || ""
          });

          const response = await fetch(
            `${apiUrl}/fpvideo/delete_content?${params.toString()}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (result.success) {
            message.success("Content deleted successfully!");
            setRefreshing(true);
            refreshContent();
          } else {
            message.error(result.message || "Failed to delete content");
          }
        } catch (error) {
          message.error("Failed to delete content");
        }
      },
    });
  };

  // Upload controls
  const pauseUpload = () => {
    if (currentUploader) {
      currentUploader.pause();
      message.info("Upload paused");
    }
  };

  const resumeUpload = () => {
    if (currentUploader) {
      currentUploader.resume();
      message.info("Upload resumed");
    }
  };

  const abortUpload = () => {
    if (currentUploader) {
      currentUploader.abort();
      setUploading(false);
      setUploadProgress(0);
      setCurrentUploader(null);
      message.info("Upload aborted");
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <Title level={2}>
        <FileTextOutlined /> Song Video Upload Manager
      </Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* Songs Selection */}
        <Card title="Select Song for Video Upload" size="default">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Available Songs (without playbackStatusFp): </Text>
              <Text type="secondary">({filteredSongs.length} songs found)</Text>
            </div>

            <Select
              style={{ width: '100%', maxWidth: 600 }}
              placeholder="Select a song to upload video"
              value={selectedSong?.key}
              onChange={(value) => {
                const song = filteredSongs.find(s => s.key === value);
                setSelectedSong(song);
                setContentExists(song?.playbackStatusFp ? "ready" : null);
              }}
              loading={loading}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {filteredSongs.map(song => (
                <Option key={song.key} value={song.key}>
                  {song.title || song.name || song.key}
                </Option>
              ))}
            </Select>

            {selectedSong && (
              <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                <Text strong>Selected Song Details:</Text>
                <div style={{ marginTop: 8 }}>
                  <div><Text strong>Title: </Text>{selectedSong.title}</div>
                </div>
              </div>
            )}
          </Space>
        </Card>

        {/* Video Upload Section */}
        {selectedSong && (
          <Card title="Video Upload (MP4 only)" size="default" style={{ width: 500 }}>

            <img src={`${selectedSong?.trailerImage}/public`} alt="content-image" style={{ width: "200px", height: "100px" }} />

            {refreshing ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin tip="Loading Content Updates" />
              </div>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                {contentExists === "ready" && (
                  <>
                    <Tag
                      color="green"
                      style={{
                        width: 160,
                        fontSize: 16,
                        marginTop: 20,
                        textAlign: "center",
                        padding: "5px",
                      }}
                    >
                      Content Exists
                    </Tag>

                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={uploading}
                      disabled={uploading}
                      onClick={handleDeleteContent}
                    >
                      Delete Content
                    </Button>
                  </>
                )}

                {contentExists === "processing" && (
                  <Tag
                    color="green"
                    style={{
                      width: 160,
                      fontSize: 16,
                      marginTop: 20,
                      marginBottom: 20,
                      textAlign: "center",
                      padding: "5px",
                    }}
                  >
                    Content Processing
                  </Tag>
                )}

                {contentExists === "failed" && (
                  <Tag
                    color="red"
                    style={{
                      width: 140,
                      fontSize: 16,
                      marginTop: 20,
                      marginBottom: 20,
                      textAlign: "center",
                    }}
                  >
                    Content Failed, Try Re-uploading
                  </Tag>
                )}

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={uploading}
                  disabled={uploading}
                  onClick={() =>
                    document.getElementById("videoUpload").click()
                  }
                >
                  {["ready", "processing"].includes(contentExists)
                    ? "Re-Upload Video"
                    : "Upload Video"}
                </Button>

                <input
                  type="file"
                  id="videoUpload"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleVideoUpload(file);
                    }
                  }}

                  accept="video/mp4"
                  disabled={uploading}
                  style={{ display: "none" }}
                />

                {uploading && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Progress percent={uploadProgress} />
                    <Space>
                      <Button size="small" onClick={pauseUpload}>
                        Pause
                      </Button>
                      <Button size="small" onClick={resumeUpload}>
                        Resume
                      </Button>
                      <Button size="small" danger onClick={abortUpload}>
                        Abort
                      </Button>
                    </Space>
                  </Space>
                )}
              </Space>
            )}
          </Card>
        )}

        {/* Legacy FastPix Keys Removal */}
        {/* <Card title="Legacy: Remove FastPix Keys" size="default">
          <Button type="button" loading={loading} onClick={(e) =>  removeFastPixKeys(e)}>
            Remove FastPix Keys
          </Button>
        </Card> */}
      </Space>
    </div>
  );
};

export default ManageContentIds;
